"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { Check, MessageSquareText } from "lucide-react";
import { motion } from "motion/react";
import axios from "@/app/api/axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { cn, formatCurrency } from "@/lib/utils";
import { trackButtonClick, trackLead } from "@/lib/gtag";

export const CUSTOM_PLAN_CONTACT_HREF = "/contact?topic=custom-plan";

// Compact cards only list the first few (most important) features.
const COMPACT_FEATURE_COUNT = 3;

const customPlan = {
  slug: "custom",
  title: "Custom",
  is_custom: true,
  display_features: [
    "Everything in Pro",
    "More than 10 team members",
    "Volume pricing for your team",
    "Multi-location setup support",
    "Dedicated onboarding & data migration",
    "Priority support",
  ],
};

const buildCustomPlan = (plans) => ({
  ...customPlan,
  // Mirrors the top paid plan so every feature it unlocks also shows as included here.
  entitlements_map: plans[plans.length - 1]?.entitlements_map ?? {},
});

// Free monthly text credits (e.g. "250 text credits / month included") go
// right after the team members line, or last when there is none.
export const withTextCredits = (plan) => {
  const features = plan.display_features ?? [];
  if (!plan.text_credits_description) return features;

  const teamIndex = features.findIndex((feature) =>
    /team member/i.test(String(feature)),
  );
  const at = teamIndex === -1 ? features.length : teamIndex + 1;

  return [
    ...features.slice(0, at),
    plan.text_credits_description,
    ...features.slice(at),
  ];
};

export const planColors = [
  "bg-blue-500/30",
  "bg-orange-500/30",
  "bg-teal-400/30",
  "bg-purple-400/30",
  "bg-rose-400/30",
];

export const cardVariants = {
  hidden: {
    opacity: 0,
    y: 80,
  },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.2,
      duration: 0.6,
      ease: "easeInOut",
    },
  }),
};

// Public plans from the API plus the "Custom" plan; empty until loaded.
export function usePricingPlans() {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let isMounted = true;

    async function fetchPricingPlans() {
      try {
        const { data } = await axios.get("/api/public/pricing-plans");
        const remotePlans = data?.data?.plans ?? [];

        if (isMounted) {
          setPlans(remotePlans);
        }
      } catch {
        if (isMounted) {
          setPlans([]);
        }
      }
    }

    fetchPricingPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  return useMemo(
    () => (plans.length > 0 ? [...plans, buildCustomPlan(plans)] : []),
    [plans],
  );
}

// `compact` shows smaller cards with just the top features, for pages that
// link to the full /pricing page (e.g. feature pages).
export function PricingPlans({ plans, location = "pricing_page", compact = false }) {
  const handleTalkToUsClick = () => {
    trackButtonClick("talk_to_us", { location, plan: "custom" });
    trackLead({ source: location, cta_label: "talk_to_us", plan: "custom" });
  };

  return (
    <div
      className={cn(
        "relative mb-5 grid w-full grid-cols-1 items-start justify-center px-4 sm:grid-cols-2 xl:grid-cols-4",
        compact ? "gap-4" : "gap-6",
      )}
    >
      {plans.map((items, index) => {
        const price = Number(items?.price ?? items?.display_price ?? 0);
        const comparePrice = Number(items?.compare_price ?? 0);
        const showComparePrice = comparePrice > price;
        const allFeatures = withTextCredits(items);
        const features = compact
          ? allFeatures.slice(0, COMPACT_FEATURE_COUNT)
          : allFeatures;

        return (
          <motion.div
            key={items.id ?? items.slug ?? items.title ?? index}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={index}
            className="h-full w-full"
          >
            <Card className={"h-full w-full gap-2 rounded-2xl border-0 ring-0"}>
              <CardHeader
                className={cn(
                  planColors[index % planColors.length],
                  "rounded-t-2xl",
                  compact ? "px-6 py-4" : "px-6 py-6 sm:px-10",
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className={compact ? "text-2xl" : "text-3xl"}>{items.title}</CardTitle>
                  {items.highlight && (
                    <Badge className="border-0 bg-white/80 text-neutral-900">
                      Most popular
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="h-full w-full px-0">
                <div
                  className={cn(
                    "flex flex-col px-8 sm:px-6",
                    compact ? "gap-5 pt-3 pb-6" : "gap-8 pt-4 pb-8",
                  )}
                >
                  {items.is_custom ? (
                    <div className={compact ? "space-y-1" : "space-y-3"}>
                      <div className={compact ? "h-6" : "h-[26px]"} />
                      <p
                        className={cn(
                          "text-card-foreground flex items-center font-semibold",
                          compact ? "min-h-11 text-3xl" : "min-h-14 text-4xl",
                        )}
                      >
                        Let&apos;s talk
                      </p>
                    </div>
                  ) : (
                    <div className={compact ? "space-y-1" : "space-y-3"}>
                      {showComparePrice ? (
                        <div className={cn("flex items-center", compact ? "h-6" : "h-[26px]")}>
                          <span
                            className={cn(
                              "relative inline-block font-semibold text-neutral-900",
                              compact ? "text-xl" : "text-2xl",
                            )}
                          >
                            {formatCurrency(items.compare_price)}
                            <span
                              aria-hidden="true"
                              className="absolute top-1/2 right-[-8%] left-[-8%] h-0.5 -translate-y-1/2 -rotate-6 bg-red-500"
                            />
                          </span>
                        </div>
                      ) : (
                        <div className={compact ? "h-6" : "h-[26px]"} />
                      )}
                      {/* min-h keeps every plan's CTA on the same line, even when "per month" wraps. */}
                      <p
                        className={cn(
                          "text-card-foreground flex items-center font-semibold",
                          compact ? "min-h-11 text-4xl" : "min-h-14 text-5xl",
                        )}
                      >
                        {formatCurrency(items.price ?? items.display_price)}
                        <span
                          className={cn(
                            "font-normal text-neutral-800",
                            compact ? "ms-2 text-base" : "ms-3 text-xl",
                          )}
                        >
                          per month
                        </span>
                      </p>
                    </div>
                  )}
                  {items.is_custom ? (
                    <Button
                      asChild
                      className={cn(
                        "text-md relative w-full grow rounded-md border border-neutral-800 bg-white text-neutral-800 transition-all duration-500 hover:bg-white hover:text-neutral-900 md:text-base",
                        compact ? "h-10 md:h-11" : "h-11 md:h-12",
                      )}
                    >
                      <Link
                        href={CUSTOM_PLAN_CONTACT_HREF}
                        onClick={handleTalkToUsClick}
                      >
                        <span className="relative z-10 flex items-center gap-2 transition-all duration-500">
                          <MessageSquareText
                            className="size-4"
                            aria-hidden="true"
                          />
                          Talk to us
                        </span>
                      </Link>
                    </Button>
                  ) : (
                    <Button
                      asChild
                      className={cn(
                        "text-md relative w-full grow rounded-md border border-neutral-800 bg-white text-neutral-800 transition-all duration-500 hover:bg-white hover:text-neutral-900 md:text-base",
                        compact ? "h-10 md:h-11" : "h-11 md:h-12",
                      )}
                    >
                      <Link href="/signup">
                        <span className="relative z-10 transition-all duration-500">
                          {items.is_trial_enabled === false
                            ? "Get started"
                            : "Try for free"}
                        </span>
                      </Link>
                    </Button>
                  )}
                </div>
                <Separator orientation="horizontal" />
                <div
                  className={cn(
                    "flex grow flex-col items-start gap-3 px-8 sm:px-6",
                    compact ? "my-5" : "my-8",
                  )}
                >
                  <ul
                    className={cn(
                      "flex flex-col items-start self-stretch",
                      compact ? "gap-2.5" : "gap-3",
                    )}
                  >
                    {features.map((feature, index) => {
                      return (
                        <li
                          key={index}
                          className={cn(
                            "text-card-foreground flex items-center gap-3 font-normal tracking-normal",
                            compact ? "text-sm" : "text-base",
                          )}
                        >
                          <Check size={16} aria-hidden="true" className="shrink-0" />
                          {feature}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
}
